import { Link } from 'react-router-dom';
import type { TVSeries } from '@/mocks/tvSeries';

interface TVSeriesCardProps {
  series: TVSeries;
  variant?: 'default' | 'horizontal' | 'compact';
}

export default function TVSeriesCard({ series, variant = 'default' }: TVSeriesCardProps) {
  if (variant === 'horizontal') {
    return (
      <Link
        to={`/detail/${series.id}`}
        className="group flex gap-3 p-3 rounded-xl bg-background-50 hover:bg-background-100 transition-all"
      >
        <div className="w-20 h-28 flex-shrink-0 rounded-lg overflow-hidden">
          <img
            src={series.cover}
            alt={`${series.title} 天美影视剧集海报`}
            title={`${series.title} - 天美影视高清在线观看`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        </div>
        <div className="flex flex-col justify-between py-1 min-w-0">
          <div>
            <h3 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
              {series.title}
            </h3>
            <p className="text-xs text-foreground-500 mt-1 line-clamp-2">{series.description}</p>
          </div>
          <div className="flex items-center gap-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-star-fill text-accent-500 text-xs"></i>
              {series.rating}
            </span>
            <span>{series.year}</span>
            <span>{series.episodes}集</span>
            <span className={series.status === '更新中' ? 'text-primary-500' : 'text-foreground-400'}>
              {series.status}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/detail/${series.id}`}
        className="group"
      >
        <div className="aspect-[3/4] rounded-lg overflow-hidden">
          <img
            src={series.cover}
            alt={`${series.title} 天美影视剧集海报`}
            title={`${series.title} - 天美影视高清在线观看`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
            loading="lazy"
          />
        </div>
        <div className="mt-2">
          <h3 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
            {series.title}
          </h3>
          <div className="flex items-center gap-2 mt-1 text-xs text-foreground-500">
            <span className="flex items-center gap-0.5">
              <i className="ri-star-fill text-accent-500 text-[10px]"></i>
              {series.rating}
            </span>
            <span>{series.year}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${series.id}`}
      className="group block rounded-xl overflow-hidden bg-background-50 hover:bg-background-100 transition-all"
    >
      <div className="relative aspect-[3/4] overflow-hidden">
        <img
          src={series.cover}
          alt={`${series.title} 天美影视剧集海报`}
          title={`${series.title} - 天美影视高清在线观看`}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
          <span className="text-white text-xs font-medium bg-primary-500 px-2 py-1 rounded-md whitespace-nowrap">
            立即观看
          </span>
        </div>
        {series.status === '更新中' && (
          <span className="absolute top-2 left-2 bg-primary-500 text-background-50 text-[10px] font-medium px-1.5 py-0.5 rounded whitespace-nowrap">
            更新中
          </span>
        )}
        {series.rating >= 9 && (
          <span className="absolute top-2 right-2 bg-accent-500 text-background-50 text-[10px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap">
            神作
          </span>
        )}
      </div>
      <div className="p-3">
        <h3 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
          {series.title}
        </h3>
        <div className="flex items-center gap-2 mt-1.5 text-xs text-foreground-500">
          <span className="flex items-center gap-0.5">
            <i className="ri-star-fill text-accent-500 text-[10px]"></i>
            {series.rating}
          </span>
          <span>{series.year}</span>
          <span>{series.episodes}集</span>
        </div>
      </div>
    </Link>
  );
}